/**
 *  @class
 *  @function Cart
 */
class Cart {
  constructor() {
    this.container = document.getElementById('Cart');

    if (!this.container) return;

    // Store the element that opened the cart drawer
    this.cartOpenTrigger = null;

    // Prevent repeated focus when drawer is already open
    this.wasCartOpen = false;

    // Make cart drawer accessible and focusable
    this.container.setAttribute('role', 'dialog');
    this.container.setAttribute('aria-modal', 'true');
    this.container.setAttribute('aria-label', 'Shopping cart');
    this.container.setAttribute('tabindex', '-1');

    this.setupEventListeners();
  }

  onChange(event) {
    if (event.target.type == 'number') {
      this.updateQuantity(event.target.dataset.index, event.target.value);
    } else if (event.target.getAttribute('id') == 'CartSpecialInstructions') {
      this.saveNotes();
    }
  }

  saveNotes() {
    const cartNotes = document.getElementById('CartSpecialInstructions');

    if (!cartNotes) return;

    fetch(`${theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': `application/json`
      },
      body: JSON.stringify({
        note: cartNotes.value
      })
    });
  }

  setupEventListeners() {
    this.removeProductEvent();

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    document.addEventListener('cart:refresh', () => {
      this.refresh();
    });

    this.container.addEventListener('change', this.debouncedOnChange.bind(this));

    this.termsCheckbox();

    /*
      Store opener before cart drawer opens.
      This supports cart icon, cart links, add-to-cart buttons,
      and buttons that may open the cart drawer after AJAX add.
    */
    document.addEventListener('click', (event) => {
      const cartOpenButton = event.target.closest(
        `
          #cart-drawer-toggle,
          .cart-drawer-toggle,
          .thb-cart-drawer-toggle,
          .header__icon--cart,
          a[href="/cart"],
          a[href*="/cart"],
          button[name="add"],
          button[type="submit"][name="add"],
          .product-form__submit,
          .single-add-to-cart-button
        `
      );

      if (cartOpenButton && !this.container.contains(cartOpenButton)) {
        this.cartOpenTrigger = cartOpenButton;
      }
    });

    /*
      Watch cart drawer visibility.
      When it becomes visible, move focus inside.
      When it becomes hidden, return focus back.
    */
    const cartObserver = new MutationObserver(() => {
      const isOpen = this.isCartDrawerVisible();

      if (isOpen && !this.wasCartOpen) {
        this.wasCartOpen = true;

        setTimeout(() => {
          this.focusCartDrawer();
        }, 150);
      }

      if (!isOpen && this.wasCartOpen) {
        this.wasCartOpen = false;

        setTimeout(() => {
          this.returnFocusToCartTrigger();
        }, 150);
      }
    });

    cartObserver.observe(this.container, {
      attributes: true,
      attributeFilter: ['class', 'aria-hidden', 'style']
    });

    /*
      Also watch body class because many Shopify drawers
      control drawer visibility using body classes.
    */
    const bodyObserver = new MutationObserver(() => {
      const isOpen = this.isCartDrawerVisible();

      if (isOpen && !this.wasCartOpen) {
        this.wasCartOpen = true;

        setTimeout(() => {
          this.focusCartDrawer();
        }, 150);
      }

      if (!isOpen && this.wasCartOpen) {
        this.wasCartOpen = false;

        setTimeout(() => {
          this.returnFocusToCartTrigger();
        }, 150);
      }
    });

    bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    /*
      Close button click.
      If theme already closes drawer, we only return focus.
      If not, we also remove common open classes.
    */
    document.addEventListener('click', (event) => {
      const closeButton = event.target.closest(
        `
          .side-panel-close,
          .side-panel-close-button,
          .cart-drawer__close,
          .drawer__close,
          button[aria-label="Close"],
          button[aria-label="Close cart"],
          button.close,
          .close
        `
      );

      if (closeButton && this.container.contains(closeButton)) {
        setTimeout(() => {
          this.closeCartDrawer();
          this.returnFocusToCartTrigger();
        }, 100);
      }
    });

    /*
      Escape key close and focus return.
    */
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isCartDrawerVisible()) {
        event.preventDefault();

        this.closeCartDrawer();

        setTimeout(() => {
          this.returnFocusToCartTrigger();
        }, 150);
      }
    });
  }

  isCartDrawerVisible() {
    return (
      this.container.classList.contains('active') ||
      this.container.classList.contains('open') ||
      this.container.getAttribute('aria-hidden') === 'false' ||
      document.body.classList.contains('open-cc') ||
      document.body.classList.contains('cart-drawer-open') ||
      document.body.classList.contains('cart-open')
    );
  }

  focusCartDrawer() {
    if (!this.isCartDrawerVisible()) return;

    this.container.focus({
      preventScroll: true
    });

    const firstFocusableElement = this.container.querySelector(
      `
        button:not([disabled]),
        [href],
        input:not([disabled]),
        select:not([disabled]),
        textarea:not([disabled]),
        [tabindex]:not([tabindex="-1"])
      `
    );

    if (firstFocusableElement) {
      firstFocusableElement.focus({
        preventScroll: true
      });
    }
  }

  returnFocusToCartTrigger() {
    if (
      this.cartOpenTrigger &&
      document.body.contains(this.cartOpenTrigger) &&
      typeof this.cartOpenTrigger.focus === 'function'
    ) {
      this.cartOpenTrigger.focus({
        preventScroll: true
      });
    }
  }

  closeCartDrawer() {
    this.container.classList.remove('active');
    this.container.classList.remove('open');
    this.container.setAttribute('aria-hidden', 'true');

    document.body.classList.remove('open-cc');
    document.body.classList.remove('cart-drawer-open');
    document.body.classList.remove('cart-open');

    dispatchCustomEvent('cart:close');
  }

  getSectionsToRender() {
    return [
      {
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }
    ];
  }

  displayErrors(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);

    if (lineItemError) {
      lineItemError.removeAttribute('hidden');

      const errorText = lineItemError.querySelector('.cart-item__error-text');

      if (errorText) {
        errorText.innerHTML = message;
      }

      const cartItem = this.container.querySelector(`#CartItem-${line}`);

      if (cartItem) {
        cartItem.classList.remove('loading');
      }
    }
  }

  getSectionInnerHTML(html, selector) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const selectedElement = parsedHTML.querySelector(selector);

    return selectedElement ? selectedElement.innerHTML : '';
  }

  updateQuantity(line, quantity) {
    this.container.classList.add('cart-disabled');

    if (line && this.container.querySelector(`#CartItem-${line}`)) {
      this.container.querySelector(`#CartItem-${line}`).classList.add('loading');
    }

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    dispatchCustomEvent('line-item:change:start', {
      quantity: quantity
    });

    fetch(`${theme.routes.cart_change_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': `application/json`
      },
      body
    })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          this.displayErrors(line, parsedState.errors);
          this.container.classList.remove('cart-disabled');
          return;
        }

        this.renderContents(parsedState, line, false);

        this.container.classList.remove('cart-disabled');

        dispatchCustomEvent('line-item:change:end', {
          quantity: quantity,
          cart: parsedState
        });
      });
  }

  refresh() {
    this.container.classList.add('cart-disabled');

    let sections = 'main-cart';

    fetch(`${window.location.pathname}?sections=${sections}`)
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          this.container.classList.remove('cart-disabled');
          return;
        }

        this.renderContents(parsedState, false, true);

        this.container.classList.remove('cart-disabled');
      });
  }

  termsCheckbox() {
    let terms_checkbox = this.container.querySelector('#CartTerms');
    let checkout_button = this.container.querySelector('.button.checkout-button');

    if (terms_checkbox && checkout_button) {
      terms_checkbox.setCustomValidity(theme.strings.requiresTerms);

      checkout_button.addEventListener('click', function (e) {
        if (!terms_checkbox.checked) {
          terms_checkbox.reportValidity();
          terms_checkbox.focus();
          e.preventDefault();
        }
      });
    }
  }

  removeProductEvent() {
    let removes = this.container.querySelectorAll('.remove');

    removes.forEach((remove) => {
      remove.addEventListener('click', (event) => {
        this.updateQuantity(event.target.dataset.index, '0');
        event.preventDefault();
      });
    });
  }

  renderContents(parsedState, line, refresh) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.getElementById(section.id);

      if (!sectionElement) return;

      const elementToReplace =
        sectionElement.querySelector(section.selector) || sectionElement;

      if (refresh) {
        if (parsedState[section.section]) {
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState[section.section],
            section.selector
          );
        }
      } else {
        if (parsedState.sections && parsedState.sections[section.section]) {
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        }
      }

      this.removeProductEvent();

      if (line && this.container.querySelector(`#CartItem-${line}`)) {
        this.container.querySelector(`#CartItem-${line}`).classList.remove('loading');
      }

      this.termsCheckbox();
    });
  }
}

window.addEventListener('load', () => {
  if (typeof Cart !== 'undefined') {
    new Cart();
  }
});